// modal-manager-instance.ts
import { Modal, ModalOptions } from 'flowbite';
import axios from 'axios';

// Updated interface for the modal content structure.
export interface ModalContent {
  name: string;
  description: string;
  image?: { // Image is optional
    src: string;
    srcset?: string;
    sizes?: string;
    alt?: string;
    width?: number;
    height?: number;
  };
}

// Interface for the configuration object passed to the constructor.
// This makes the class more flexible.
export interface ManagerConfig {
  triggerClass?: string; // Optional: The CSS class for elements that trigger the modal.
  fetchUrl: string;       // Required: The URL to fetch data from.
}

// Global Axios interceptor.
// It catches and logs all errors from Axios requests throughout the application.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Global Axios Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Manages the creation and interaction of dynamically loaded modals via AJAX.
 * This class must be instantiated.
 * e.g., const myManager = new roomModal({ fetchUrl: '...' });
 * myManager.init();
 */
export class roomModal {

  // Instance properties (no longer static)
  private modalInstances: Map<string, Modal> = new Map();
  private readonly triggerClass: string;
  private readonly fetchUrl: string;
   private lastTrigger: HTMLElement | null = null;

  /**
   * The class constructor.
   * @param {ManagerConfig} config - The configuration object for this instance.
   */
  constructor(config: ManagerConfig) {
    // Set properties based on the provided configuration.
    this.triggerClass = config.triggerClass || 'modal-trigger';
    this.fetchUrl = config.fetchUrl;

    if (!this.fetchUrl) {
      throw new Error("A fetchUrl must be provided in the configuration.");
    }
  }

  /**
   * Initializes the manager using event delegation.
   * A single event listener on the document body handles clicks on all trigger elements,
   * which is more performant, especially for dynamically added content.
   */
  public init(): void {
    document.body.addEventListener('click', async (event: MouseEvent) => {
        
        // Find the closest ancestor (or the element itself) that matches the trigger class.
        const triggerElement = (event.target as HTMLElement).closest(`.${this.triggerClass}`);

        // If the click was not on a trigger element or one of its children, do nothing.
        if (!triggerElement) {
            return;
        }

        // Prevent the default action for links or buttons if needed.
        event.preventDefault();
        
        // Store the trigger element to return focus to it later.
        this.lastTrigger = triggerElement as HTMLElement;

        const modalId = triggerElement.getAttribute('data-modal-id') || `modal-${Math.random().toString(36).substring(2, 9)}`;
        const gameId = triggerElement.getAttribute('data-game');

        if (!gameId) {
            console.error("Missing 'data-game' attribute on the trigger element.", triggerElement);
            return;
        }

        this.showSkeleton(modalId);

        try {
            const contentData = await this.fetchModalContent(gameId);
            this.open(modalId, contentData);
        } catch (error) {
            console.error('Error fetching modal content:', error);
            this.showError(modalId, 'Failed to load content. Please try again later.');
        }
    });
  }

  /**
   * Performs the AJAX call to get the modal content from the server.
   * @param {string} gameId - The ID of the game/content to fetch.
   * @returns {Promise<ModalContent>} The data to populate the modal.
   */
  private async fetchModalContent(gameId: string): Promise<ModalContent> {
    const response = await axios.post(this.fetchUrl, {
      game_id: gameId,
    });
    
    // We assume the useful data is in response.data.data
    return response.data.data;
  }

  /**
   * Displays a skeleton UI that matches the new HTML structure while content is loading.
   * @param {string} id - The ID of the modal's wrapper element.
   */
  private showSkeleton(id: string): void {
    let wrapper = document.getElementById(id);
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = id;
      // This outer div will be the modal instance for Flowbite
      document.body.appendChild(wrapper);
    }
    
    // Skeleton HTML with a structure matching the final getHTML output.
    wrapper.innerHTML = `
        <div tabindex="-1" aria-hidden="true" class="fixed top-[10%] bottom-[10%] left-[50%] translate-x-[-50%] z-[110] w-full overflow-hidden h-full max-h-[80%] max-w-4xl px-4">
            <div class="relative flex flex-row flex-wrap basis-full w-full h-full bg-neutral-900 border border-neutral-500/70 rounded-lg shadow animate-pulse">
                <div class="absolute top-0 left-0 z-10 flex flex-row flex-wrap basis-full w-full h-20 items-center justify-between px-8 rounded-t bg-main-rose-800 border-b border-b-neutral-500/70">
                    <div class="h-6 bg-neutral-700 rounded w-1/2"></div>
                    <div class="w-8 h-8 bg-neutral-700 rounded-lg"></div>
                </div>
                <div class="flex flex-row flex-wrap basis-full w-full h-[calc(100%-80px)] mt-20 pb-5 overflow-y-auto items-start">
                    <figure class="flex flex-row flex-wrap basis-full w-full items-start px-8">
                        <div class="w-full h-48 bg-neutral-700 rounded"></div>
                    </figure>
                    <div class="flex flex-row flex-wrap basis-full w-full mt-6 px-8 items-start space-y-3">
                        <div class="h-4 bg-neutral-700 rounded w-full"></div>
                        <div class="h-4 bg-neutral-700 rounded w-full"></div>
                        <div class="h-4 bg-neutral-700 rounded w-5/6"></div>
                    </div>
                </div>
            </div>
        </div>
    `;
  }

  /**
   * Displays an error message inside the modal.
   * @param {string} id - The ID of the modal's wrapper.
   * @param {string} message - The error message to display.
   */
  private showError(id: string, message: string): void {
    const wrapper = document.getElementById(id);
    if (!wrapper) return;

    // HTML for the error display.
    wrapper.innerHTML = `
      <div class="relative w-full max-w-md max-h-full bg-white rounded-lg shadow dark:bg-gray-700 p-6">
        <h3 class="text-lg font-semibold text-red-600 dark:text-red-500">Error</h3>
        <p class="mt-4 text-sm text-gray-600 dark:text-gray-300">${message}</p>
        <div class="mt-6 flex justify-end">
          <button id="close-error-${id}" class="text-white bg-red-600 hover:bg-red-700 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-4 py-2">
            Close
          </button>
        </div>
      </div>
    `;
    // Add a listener to close the error modal.
    document.getElementById(`close-error-${id}`)?.addEventListener('click', () => {
        // We get the Flowbite instance and hide it, which removes the backdrop.
        this.modalInstances.get(id)?.hide();
    });
  }

  /**
   * Opens and displays the modal with the final content.
   * @param {string} id - The ID of the modal's wrapper.
   * @param {ModalContent} content - The object containing the data to show.
   */
  public open(id: string, content: ModalContent): void {
    let wrapper = document.getElementById(id);
    if (!wrapper) {
      // This should not happen if showSkeleton was called, but it's a safe fallback.
      wrapper = document.createElement('div');
      wrapper.id = id;
      document.body.appendChild(wrapper);
    }
    
    // Inject the final HTML into the modal wrapper.
    wrapper.innerHTML = this.getHTML(content);
    
    // The actual element that Flowbite will manage is the one with tabindex.
    const modalElement = wrapper.querySelector<HTMLElement>('[tabindex="-1"]');
    if (!modalElement) return;

    // If a Flowbite instance for this modal already existed, hide it.
    if (this.modalInstances.has(id)) {
        this.modalInstances.get(id)?.hide();
    }
    
    // Accessibility fix: Options to return focus on hide
      const modalOptions: ModalOptions = {
        backdrop: 'static',
        onHide: () => {
            // When the modal hides, return focus to the element that opened it.
            this.lastTrigger?.focus();

            // Find the wrapper element to remove it from the DOM
            const wrapperToRemove = document.getElementById(id);
            if(wrapperToRemove) {
                wrapperToRemove.remove();
            }

            // Clean up the instance from the map to prevent memory leaks
            this.modalInstances.delete(id);
        }
    };
    
    // Create a new Flowbite modal instance with the options.
    const modal = new Modal(modalElement, modalOptions);
    this.modalInstances.set(id, modal);
    modal.show();

    // Add event listeners for any element that should close the modal.
    wrapper.querySelectorAll('[data-modal-cancel]').forEach(btn => {
        btn.addEventListener('click', () => {
            modal.hide();
        });
    });
  }

  /**
   * Generates the final HTML for the modal.
   * @param {ModalContent} content - The object containing data to populate.
   * @returns {string} The HTML string for the modal.
   */
  private getHTML(content: ModalContent): string {
    
    let imageHTML = '';
    // Check if image data exists and has a source URL.
    if (content.image && content.image.src) {
        imageHTML = `
            <img 
                src="${content.image.src}" 
                srcset="${content.image.srcset || ''}" 
                sizes="${content.image.sizes || ''}"
                alt="${content.image.alt || content.name}"
                class="w-full object-cover" 
                loading="lazy"
                width="${content.image.width || ''}"
                height="${content.image.height || ''}" 
            />`;
    }

    // HTML structure based on Flowbite components, adapted for the new content.
    return `
      <div tabindex="-1" aria-hidden="true" class="fixed top-[10%] bottom-[10%] left-[50%] translate-x-[-50%] z-[110] w-full overflow-hidden h-full max-h-[80%] max-w-4xl px-4">

        <div class="relative flex flex-row flex-wrap basis-full w-full h-full bg-neutral-900 border border-neutral-500/70 rounded-lg shadow">

            <div class="absolute top-0 left-0 z-10 flex flex-row basis-full w-full h-20 items-center justify-between space-x-2 px-4 sm:px-8 rounded-t bg-main-rose-800 border-b border-b-neutral-500/70">

                <strong class="fluid-text-base font-semibold text-white">
                    ${content.name}
                </strong>

                <button type="button" class="rounded-lg fluid-text-sm p-1.5 ml-auto inline-flex items-center bg-neutral-900 text-white hover:bg-neutral-800" data-modal-cancel>
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
                </button>

            </div>

            <div class="flex flex-row flex-wrap basis-full w-full h-[calc(100%-80px)] min-h-40 mt-20 pb-5 overflow-y-auto items-start">

                <figure class="flex flex-row flex-wrap basis-full w-full items-start">
                    ${imageHTML}
                </figure>

                <div class="flex flex-row flex-wrap basis-full w-full mt-6 px-4 sm:px-8 items-start">

                    <div class="flex flex-row flex-wrap basis-full w-full items-start">

                        <div class="prose max-w-none prose-p:fluid-text-base prose-strong:font-bold prose-p:text-white prose-strong:text-white text-white">
                            ${content.description}
                        </div>

                    </div>
                    
                </div>

            </div>

        </div>

      </div>
    `;
  }
}
